import type { Pattern } from "./patterns";
import { cmp, mul, ratio, type Ratio } from "./ratio";
import type { CountedSyllable } from "./syllables";
import type { TalaGrid } from "./talaGrid";

/** A pattern as the stroke lane draws it, over the tala's counting line. */
export interface Lane {
  name: string;
  source: string;
  /** How many cells the cycle divides into. */
  aksharas: number;
  /** How many slots each cell divides into, fine enough for every stroke and syllable to sit in one. */
  columns: number;
  /** Each stroke in the pattern's order, with where it falls in the cycle (`at`) and where it sits in the lane. */
  strokes: { stroke: string; at: Ratio; akshara: number; column: number }[];
  counting: { syllable: string; akshara: number; column: number }[];
  /**
   * The akshara each row starts at, from 0: a row per anga, since a cycle
   * reads as its claps and waves, and a long one (Dhruva's 14 aksharas) then
   * fits a phone without scrolling sideways. One row when the cycle can't be
   * split that way.
   */
  rows: number[];
}

// Past this many slots a cell is too narrow to read, so positions round to
// the nearest slot of the counting line instead.
const MAX_COLUMNS = 12;

/**
 * The pattern laid out for the lane: which akshara each stroke and counting
 * syllable falls in, and which slot of it. Positions are fractions of the
 * cycle, so this is the one place that decides how a cycle is divided for
 * reading.
 */
export function laneFor(pattern: Pattern | null, counting: CountedSyllable[], grid?: TalaGrid): Lane | null {
  if (!pattern || pattern.strokes.length === 0) return null;
  const aksharas = Math.max(1, pattern.aksharas);
  const place = (at: Ratio) => {
    const pos = mul(at, ratio(aksharas));
    const akshara = Math.min(aksharas - 1, Math.floor(pos.n / pos.d));
    return { akshara, within: ratio(pos.n - akshara * pos.d, pos.d) };
  };

  const strokes = pattern.strokes.map((s) => ({ stroke: s.stroke, at: s.at, ...place(s.at) }));
  const said = counting.map((c) => ({ syllable: c.syllable, ...place(c.at) }));

  let columns = [...strokes, ...said].reduce((cols, p) => lcm(cols, p.within.d), 1);
  if (columns > MAX_COLUMNS) columns = Math.max(1, Math.ceil(said.length / aksharas));
  const column = (within: Ratio) => Math.min(columns - 1, Math.round((within.n / within.d) * columns));

  return {
    name: pattern.name,
    source: pattern.source,
    aksharas,
    columns,
    strokes: strokes.map((s) => ({ stroke: s.stroke, at: s.at, akshara: s.akshara, column: column(s.within) })),
    counting: said.map((c) => ({ syllable: c.syllable, akshara: c.akshara, column: column(c.within) })),
    rows: rowsFor(aksharas, grid),
  };
}

/**
 * Where each anga starts: every beat the tala claps on (a laghu, drutam or
 * anudrutam opens with one, its image "down"). Only for a pattern written an
 * akshara per beat; a chaapu is one beat in our tables but seven aksharas in
 * its pattern, and stays one row, which fits.
 */
function rowsFor(aksharas: number, grid: TalaGrid | undefined): number[] {
  if (!grid || grid.beats.length !== aksharas) return [0];
  const starts = grid.beats.flatMap((b, i) => (i === 0 || b.image === "down" ? [i] : []));
  return starts.length > 0 ? starts : [0];
}

/**
 * The scrollLeft that shows a cell in a row scrolled to `scrollLeft` and
 * `width` wide, moving as little as it can, or `scrollLeft` when the cell is
 * already in view. Positions are from the start of the row's content. For a
 * row too wide for the screen (a sankeerna laghu's nine cells), so the
 * stroke being heard stays in view without the page itself scrolling.
 */
export function scrollToShow(scrollLeft: number, width: number, cellLeft: number, cellWidth: number): number {
  if (cellLeft < scrollLeft) return cellLeft;
  if (cellLeft + cellWidth > scrollLeft + width) return cellLeft + cellWidth - width;
  return scrollLeft;
}

/**
 * The strokes to light when stroke `index` is heard: it and every stroke at
 * the same moment, as a pair's two halves are. Each half queues its own cue,
 * and the later one wins the frame, so lighting by moment rather than by
 * index keeps both lit whichever arrived last.
 */
export function litStrokes(lane: Lane, index: number | null): Set<number> {
  const heard = index === null ? undefined : lane.strokes[index];
  if (!heard) return new Set();
  return new Set(lane.strokes.flatMap((s, i) => (cmp(s.at, heard.at) === 0 ? [i] : [])));
}

function lcm(a: number, b: number): number {
  let x = a;
  let y = b;
  while (y) [x, y] = [y, x % y];
  return (a / x) * b;
}
